import classnames from 'classnames';
import React, { useEffect, useState, useRef, ChangeEvent } from 'react';
import { useId } from "react-id-generator";

import MetsIcon from 'commons/components/MetsIcon';

interface ItemSelectProps {
    /** CSS class name. This will apply on the top level container element of the component. */
    className?: string,
    /** Whether or not the component should be disabled. */
    disabled?: boolean,
    /** Indicates that the widget is in an error state. */
    error?: boolean,
    /** ID prefix for the IDs that will be assigned to various elements. Also used for the testIDs. If null, an ID will be generated. */
    idPrefix?: string,
    /** The items to display in the select. */
    items: any,
    /** An optional name that represents a field name. It will be added as data to the widget element. */
    name?: string,
    /** onBlur handler of the select. Fired when widget is blurred. */
    onBlur?: (e: React.FocusEvent<HTMLDivElement, Element>) => void,
    /** onChange handler of the select. Fired when the value changes. */
    onChange?: (e: ChangeEvent) => void,
    /** onFocus handler of the select. Fired when the widget receives focus. */
    onFocus?: (e: React.FocusEvent<HTMLDivElement, Element>) => void,
    /** onItemSelect handler of the select. Fired when the user selects an item, before the state is updated. */
    onItemSelect?: (item: any) => void,
    /** onWidgetClick handler of the select. Fired when the widget is clicked. */
    onWidgetClick?: (e: React.MouseEvent) => void,
    /** The placeholder value to display on the select when no item is selected. */
    placeholder?: string,
    /** Indicates if the widget is in readonly mode. */
    readOnly?: boolean,
    /** The value of the item that should be selected by default. */
    selectedItem?: string,
    /** Indicates whether or not to display the arrow icon. */
    showIcon: boolean,
    /** The tab index of the widget. Defaults to -1. */
    tabIndex?: number,
    /** Reference for the widget root. */
    widgetRef?: any
}

/**
 * Item Select component that displays a drop down type of widget that allows users to choose items from a list.
 * 
 * @param {object} props 
 */
function ItemSelect(props: ItemSelectProps) {
    
    // Flag that keeps track of whether or not the widget has focus.
    const [hasFocus, setHasFocus] = useState(false);
    // Flag that indicates whether or not the items drop down is visible.
    const [isItemsVisible, setIsItemsVisible] = useState(false);
    // The item that was selected by the user.
    const [selectedItem, setSelectedItem] = useState({ label: null, index: null, value: null });
    // The CSS class names that will be applied to the user.
    const widgetClassname = classnames('item-select-container', { 'error': props.error }, { 'disabled': props.disabled }, { 'readonly': props.readOnly }, { 'has-focus': hasFocus }, props.className);
    // When the user types while the widget has focus, we will want to automatically select the item for them, so this variable keeps track of what has been typed.
    const [typedString, setTypedString] = useState('');
    // Create a refs for each item that was supplied and store into an array.
    const listRefs = props.items.map(() => {
        return React.createRef();
    });

    const widgetRef = props.widgetRef !== undefined ? props.widgetRef : useRef();
    const idPrefix = props.idPrefix || useId(1, 'itemSelect-')[0];

    // If a selected item was specified...
    if (props.selectedItem !== null) {

        // If we were passed an empty string, then clear the selectedItem if it is not already set to a null value.
        if (props.selectedItem === '' && (selectedItem.value !== null)) {
            setSelectedItem({ label: null, index: null, value: null });
        } else {
            
            // Loop on all items.
            props.items.forEach((item: any, index: number) => {

                // Check if the value supplied matches the item's value, if so, select it if it is not already the currently selected item.
                if ((item.value == props.selectedItem) && (selectedItem.value != props.selectedItem)) {
                    setSelectedItem({ ...item, index });
                }

            });
        }

    }

    /**
     * Sets the scroll position of the items list.
     * 
     * @param {number} currentIndex 
     */
    const setItemListScrollPosition = (currentIndex: number) => listRefs[currentIndex].current.parentElement.scrollTop = listRefs[currentIndex].current.offsetTop;

    /**
     * When the user clicks on the widget, this function is fired.
     */
    const widgetClickHandler = (e: React.MouseEvent) => {

        if (!props.disabled && !props.readOnly) {

            if (props.onWidgetClick) {
                props.onWidgetClick(e);
            }

            widgetRef.current.focus();

            // Toggle isItemsVisible flag.
            setIsItemsVisible(!isItemsVisible);

        }

    };

    /**
     * Fired whenever the user selects an item from the list.
     * The supplied newSelectedItem should look like this:
     * 
     * { label: 'Text', index: 0, value: 'value' }
     * 
     * It's important to include the index of the selected item relative to the entire list as a property.
     * 
     * @param {object} newSelectedItem 
     */
    const selectItem = (newSelectedItem: any) => {

        if (props.onItemSelect) {
            props.onItemSelect(newSelectedItem);
        }

        if (props.onChange && listRefs[newSelectedItem.index]?.current.dataset.itemValue != selectedItem.value) {
            props.onChange(newSelectedItem);
        }

        setSelectedItem(newSelectedItem);

    };

    /**
     * When the user clicks on an item in the list, this function is fired.
     * 
     * @param {object} e 
     * @param {number} index
     */
    const itemClickHandler = (e: any, index: number) => {

        const newSelectedItem = { label: e.target.textContent, index: index, value: e.target.dataset.itemValue };

        selectItem(newSelectedItem);
        
        setIsItemsVisible(false);

        e.stopPropagation();

    };

    let keyDownTimer: any;
    const keyDownTypingInterval = 1000;

    /**
     * When the widget has focus and the user types using a keyboard, this function is fired.
     * 
     * @param {object} e 
     */
    const handleKeyDown = (e: any) => {

        let currentIndex = selectedItem.index !== null ? selectedItem.index : -1;

        if (e.key === 'ArrowDown') {

            ++currentIndex;

            // If at the end of the list, go back to the beginning when hitting down.
            if (currentIndex >= props.items.length) {
                currentIndex = 0;
            }

            selectItem({ ...props.items[currentIndex], index: currentIndex });
            setItemListScrollPosition(currentIndex);

        } else if (e.key === 'ArrowUp') {

            --currentIndex;

            // If at the beginning of the list, go to the end of the list when hitting up.
            if (currentIndex < 0) {
                currentIndex = props.items.length - 1;
            }

            selectItem({ ...props.items[currentIndex], index: currentIndex });
            setItemListScrollPosition(currentIndex);

        } else if (e.key === 'Enter') {
            setIsItemsVisible(false);
        } else if (e.key === 'Tab') {
            e.target.blur();
        } else {

            // For any other key, we will try and match what they are typing to an item text, so update the typed string and clear timeout.

            clearTimeout(keyDownTimer);

            // Set a timeout, after the time elapses, the string that the user is typing will be cleared.
            // This acts as a delay in case the user is typing a desired value, and then stops typing. 
            // They may begin typing another value again. For example, in a list of countries, I might type 
            // "Cam" which would then select "Cambodia" for me, but if I really wanted to select "Canada", 
            // I just need to stop typing momentarily and then start typing again, correcting my typo, I can 
            // then enter "Can" and have "Canada" selected for me. This is the point of clearing the typedString 
            // after the timeout expires. Every time the user hits a key however, we clear the timeout, so as long 
            // as they keep typing fast enough before the timeout interval hits, they can keep adding to their string.
            keyDownTimer = setTimeout(() => {
                setTypedString('');
            }, keyDownTypingInterval);

            // Update the typed string with what was already there + the key they just hit.
            setTypedString(typedString + e.key);

        }

        e.preventDefault();
        e.stopPropagation();

    };

    /**
     * When the user focus comes to the widget, this event is fired.
     * 
     * @param {object} e 
     */
    const handleFocus = (e: React.FocusEvent<HTMLDivElement, Element>) => {

        if (props.onFocus) {
            props.onFocus(e);
        }

        setHasFocus(true);

    }

    /**
     * When the user focus leaves the widget, this event is fired.
     * 
     * @param {object} e 
     */
    const handleBlur = (e: React.FocusEvent<HTMLDivElement, Element>) => {

        if (props.onBlur) {
            props.onBlur(e);
        }

        setIsItemsVisible(false);
        setHasFocus(false);

    };

    // Whenever the typedString changes, we will see if we can match one of the items from the list and set it as the selectedItem.
    useEffect(() => {

        if (typedString.length > 0) {

            props.items.some((item: any, index: number) => {
                if (item.label.toLowerCase().startsWith(typedString)) {
                    // Set the selectedItem to the first match.
                    selectItem({ ...item, index: index });
                    // Set the scroll position of the inner div to the corresponding ref.
                    setItemListScrollPosition(index);
                    // Found our match, so return.
                    return true;
                }
            });

        }

    }, [typedString]);

    /**
     * Renders the icon visible in the widget.
     */
    const renderIcon = () => {

        if (props.error) {
            return <span className="item-select-icon"><MetsIcon name="svg_error" className="input-field-error-icon" /></span>;
        } else {
            return props.showIcon && !props.readOnly && <span className="item-select-icon"><i className={"arrow " + (isItemsVisible && !props.disabled && !props.readOnly ? "up" : "down")}></i></span>;
        }

    };

    return (
        <div 
            className={widgetClassname}
            data-name={props.name}
            data-selected-value={selectedItem.value}
            data-testid={idPrefix + "ItemSelect"}
            id={idPrefix + "ItemSelect"}
            onBlur={handleBlur}
            onClick={widgetClickHandler}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onMouseDown={(e) => {
                /*
                 * This onMouseDown event is needed in order to support IE11, otherwise when choosing an item,
                 * IE11 will cause the onBlur of the parent div to fire before the onClick 
                 * of this item's div is clicked. Once the blur happens, the item drop down is 
                 * hidden and the item is never actually clicked. This would make it impossible 
                 * to use the mouse to select any item in IE11. By preventing the default 
                 * onMouseDown of the item, we prevent the parent's blur from being called and thus the 
                 * item can be clicked successfully in IE11 (mousedown occurs before blur and click).
                 * This also fixes not being able to click the scrollbar with the mouse to scroll items in IE11.
                 */
                e.preventDefault();
            }}
            ref={widgetRef}
            tabIndex={props.tabIndex}>
            <span className="item-select-label">{selectedItem.label ? selectedItem.label : props.placeholder}</span>
            {renderIcon()}
            <div className="item-select-items" style={{ display: isItemsVisible && !props.disabled && !props.readOnly ? "" : "none" }}>
                {props.items && props.items.length > 0 && props.items.map((item: any, index: number) => {

                    return (
                        <div
                            className={"item-select-item" + (item.value === selectedItem.value ? ' selected' : '')}
                            data-item-value={item.value}
                            key={index}
                            onClick={(e) => { itemClickHandler(e, index); }}
                            ref={listRefs[index]}>
                            {item.label}
                        </div>
                    );

                })}
            </div>
            {/* There is an underlying <select> here so that we have support for browser autofill and easier unit testing. */}
            <select
                disabled={props.disabled}
                id={idPrefix + "Select"}
                data-testid={idPrefix + "Select"}
                name={props.name}
                onChange={(e) => {
                    selectItem({ ...props.items[e.target.selectedIndex], index: e.target.selectedIndex });
                }}
                style={{ display: 'none' }}
                value={selectedItem.value || ''}
            >
                {props.items && props.items.length > 0 && props.items.map((item: any, index: number) => {
                    return <option key={index} value={item.value}>{item.label}</option>
                })}
            </select>
        </div>
    );

}

ItemSelect.defaultProps = {
    showIcon: true,
    tabIndex: 0
};

export default ItemSelect;